import React, { useContext, useRef, useEffect } from "react";
import { BsTrash } from "react-icons/bs";
import gsap from "gsap";
import { Draggable } from "gsap/Draggable";
import { timeElapsed } from "../../services/getTimeSended";
import { jeSupprimeLeMessageInbox } from "../../services/sendMessageInbox";
import { jeSupprimeLeMessageDeGroupe } from "../../services/sendMessageGroupe";
import { AuthContext } from "../../contexts/AuthContext";
import { WorkspaceContext } from "../../contexts/WorkspaceContext";
import { DisplayDiscussionForMiniScreenContext } from "../../contexts/DisplayDiscussionForMiniScreenContext";
import { MessageContext } from "../../contexts/MessageContext";
import AudioPlayer from "../audio/AudioPlayer";
import AlertError from "./AlertError";
import MeetingInviteCard from "./MeetingInviteCard";
import FileGrid from "./FileGrid";
import ExpandableText from "./ExpandableText";
import { normalizeFileContenu } from "../../services/normalizeContenu";
import { disintegrateElement } from "../../services/disintegrate";
import { includesId } from "../../services/idUtils";
import "./chat-reply.css";

gsap.registerPlugin(Draggable);

const SWIPE_THRESHOLD = 50;

function ReplyQuoteBlock({ replyToId, replyToContenu, replyToSender, replyToNature }) {
  if (!replyToId) return null;
  const isAudio = replyToNature === "messageVocal";
  const authorName = isAudio ? "Message audio" : (replyToSender?.displayName || "Vous");
  const preview = replyToContenu || "";
  const label = replyToNature === "fichier" ? "Fichier" : replyToNature === "lien" ? "Lien" : replyToNature === "code_meeting" ? "Réunion" : replyToNature === "messageVocal" ? "Message audio" : "";

  return (
    <div className="chat-reply-quote">
      <span className="chat-reply-quote-author">{isAudio ? label : authorName}</span>
      {!isAudio && (
        <span className="chat-reply-quote-text">
          {label && !preview ? label : preview.length > 80 ? preview.slice(0, 80) + "..." : preview}
        </span>
      )}
    </div>
  );
}

export default function MyMessage({
  contenu,
  object,
  idx,
  onOpenGallery,
  onDelete,
  onSwipeReply,
}) {
  const { visibleMessageErrorForUser } = useContext(MessageContext);
  const { user } = useContext(AuthContext);
  const { currentUserRole, currentWorkspace } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?._id;
  const isGuest = currentUserRole === "guest";
  const { destinataire } = useContext(DisplayDiscussionForMiniScreenContext);
  const bubbleRef = useRef(null);
  const rowRef = useRef(null);

  const vocalContent = object.nature === "messageVocal" ? (Array.isArray(object.contenu) ? object.contenu[0] : object.contenu) : null;

  useEffect(() => {
    if (!rowRef.current || !onSwipeReply) return;

    const el = rowRef.current;
    const drag = Draggable.create(el, {
      type: "x",
      bounds: { minX: 0, maxX: 80 },
      edgeResistance: 0.85,
      onDragEnd() {
        const x = this.x;
        if (x >= SWIPE_THRESHOLD && object) {
          onSwipeReply(object);
        }
        gsap.to(el, { x: 0, duration: 0.25, ease: "power2.out" });
      },
    })[0];

    return () => drag.kill();
  }, [onSwipeReply, object]);

  return (
    <div
      className={`tyn-reply-item outgoing ${
        includesId(object.deletedBy, user.userId) ? "d-none" : ""
      }`}
      style={{position:"relative !important", zIndex:"5 !important"}}
    >
      <div className="tyn-reply-group">
        <div className="chat-swipe-wrapper">
          {object.replyToId && (
            <ReplyQuoteBlock
              replyToId={object.replyToId}
              replyToContenu={object.replyToContenu}
              replyToSender={object.replyToSender}
              replyToNature={object.replyToNature}
            />
          )}
          <div
            className="tyn-reply-bubble"
            ref={bubbleRef}
            style={onSwipeReply ? { touchAction: "pan-y", userSelect: "none" } : undefined}
          >
            <div
              className="tyn-reply-text mon-text-toujours-dans-la-div"
              style={{ color: "#fff", position: "relative",zIndex:"5 !important" }}
              ref={rowRef}
            >
              {object.nature === "code_meeting" ? (
                <MeetingInviteCard code={contenu} isOwn={true} />
              ) : object.nature === "texte" ? (
                <ExpandableText>{contenu}</ExpandableText>
              ) : object.nature === "fichier" ? (
                <FileGrid
                  files={normalizeFileContenu(object.contenu)}
                  onOpenGallery={onOpenGallery}
                />
              ) : object.nature === "lien" ? (
                object.contenu[object.contenu.length - 1]
              ) : object.nature === "messageVocal" ? (
                <>
                  <AudioPlayer fileUrl={vocalContent?.fileUrl} isOwn={true} duree={vocalContent?.duree} peaks={vocalContent?.peaks} />
                  <div
                    style={{
                      position: "absolute",
                      right: "8px",
                      bottom: "0.5px",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {vocalContent?.duree > 0 && (
                      <span className="meta" style={{ fontSize: "10px" }}>
                        {Math.floor(vocalContent.duree / 60) + ":" + (Math.floor(vocalContent.duree) % 60).toString().padStart(2, "0")}
                      </span>
                    )}
                    <span className="meta" style={{ fontSize: "10px" }}>
                      {timeElapsed(object.timeUnix)}
                    </span>
                  </div>
                </>
              ) : (
                <></>
              )}
              {object.nature !== "messageVocal" && (
                <div
                  style={{
                    position: "absolute",
                    right: "8px",
                    bottom: `${object.nature === "fichier" ? "-4.8px" : "0.5px"}`,
                  }}
                >
                  <span className="meta" style={{ fontSize: "10px" }}>
                    {timeElapsed(object.timeUnix)}
                  </span>
                </div>
              )}
            </div>
            {!isGuest && (
              <ul className="tyn-reply-tools">
                <li className="dropup-center">
                  <button
                    className="btn btn-icon btn-sm btn-transparent btn-pill"
                    data-bs-toggle="dropdown"
                    onClick={() => {
                      if (bubbleRef.current) {
                        disintegrateElement(bubbleRef.current);
                      }
                      if (onDelete) onDelete(object.messageId);
                      if (typeof object.destinataire === "string") {
                        jeSupprimeLeMessageInbox(
                          user,
                          destinataire,
                          object.messageId,
                          workspaceId
                        );
                      } else {
                        jeSupprimeLeMessageDeGroupe(
                          user,
                          object.destinataire,
                          object.messageId,
                          object.canal,
                          workspaceId
                        );
                      }
                    }}
                  >
                    <BsTrash
                      style={{ height: "auto", width: "auto", fontSize: "20px" }}
                    />
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
        {idx === 0 && visibleMessageErrorForUser ? (
          <AlertError msg={visibleMessageErrorForUser} />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
